import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { clearIdentity, setIdentity, trackEvent } from "@/lib/analytics";

interface AuthCtx {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

const applyIdentity = (s: Session | null) => {
  if (s?.user) {
    const meta = (s.user.user_metadata || {}) as Record<string, unknown>;
    setIdentity({
      userId: s.user.id,
      email: s.user.email ?? null,
      name: (meta.full_name as string) || (meta.name as string) || null,
      company: (meta.company as string) || null,
    });
  } else {
    clearIdentity();
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      applyIdentity(s);
      if (event === "SIGNED_IN" && s?.user) {
        // Defer to avoid races with Supabase internals
        setTimeout(() => trackEvent("login_success", { category: "auth" }), 0);
      }
      if (event === "SIGNED_OUT") {
        clearIdentity();
      }
    });
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      applyIdentity(data.session);
      setLoading(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: "Invalid email or password." };
    return {};
  };

  const signOut = async () => {
    await trackEvent("logout", { category: "auth" });
    await supabase.auth.signOut();
  };

  return (
    <Ctx.Provider value={{ session, user: session?.user ?? null, loading, signIn, signOut }}>
      {children}
    </Ctx.Provider>
  );
};

export const useAuth = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used within AuthProvider");
  return v;
};
